import React, { forwardRef } from 'react';
import { Select } from 'antd';
import { AntdFormItem, AntdFormItemProps } from '@/components/AntdForm/AntdFormItem.tsx';
import { cn } from '@/utils/style.ts';
import { SelectGroupOption, SelectOption } from './antd-components.def.ts';
import { CurrencyOptions } from '@/types/currency.def.ts';
import { AntdFormRules } from '@/components/AntdForm/rules.ts';
import { BaseOptionType, SearchConfig } from '@rc-component/select/lib/Select';

const filterOption = (input: string, option: BaseOptionType) =>
  (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

const CustomSelect: React.FC<React.ComponentProps<typeof Select>> = forwardRef((props, ref) => {
  const showSearch =
    typeof props.showSearch === 'boolean'
      ? props.showSearch
      : ({
          ...props.showSearch,
          filterOption,
        } as SearchConfig<BaseOptionType>);

  return (
    <Select
      {...props}
      showSearch={showSearch}
      ref={ref}
      allowClear={props.allowClear ?? true}
      className={cn('w-full', props.className)}
    />
  );
});

type AntdFormSelectProps = React.ComponentProps<typeof Select> & {
  options: (SelectOption | SelectGroupOption)[];
} & AntdFormItemProps;

type AntdFormPredefinedSelectProps = React.ComponentProps<typeof Select> & AntdFormItemProps;

export const AntdFormSelect: React.FC<AntdFormSelectProps> = forwardRef(
  ({ fieldProps, options, ...props }, ref) => {
    const rulesMap = {
      selectSubset: props.mode === 'multiple' || !props.mode,
      maxCount: props.maxCount === undefined ? -1 : props.maxCount,
    };
    const finalRules = [
      ...(fieldProps.rules ?? []),
      ...(rulesMap.selectSubset ? [AntdFormRules.validSelectedValue(options)] : []),
      ...(rulesMap.maxCount >= 0 ? [AntdFormRules.maxItems(rulesMap.maxCount)] : []),
    ];

    return (
      <AntdFormItem
        {...fieldProps}
        rules={finalRules}
        extra={(fieldProps.extra ?? props.mode === 'tags') ? 'Input or select values' : undefined}
        afterLabel={
          fieldProps.afterLabel ??
          (rulesMap.maxCount >= 0 ? ` (Max. ${rulesMap.maxCount})` : undefined)
        }
      >
        <CustomSelect ref={ref} {...props} options={options} />
      </AntdFormItem>
    );
  },
);

export const AntdFormCurrency: React.FC<AntdFormPredefinedSelectProps> = forwardRef(
  ({ fieldProps, ...props }, ref) => {
    return (
      <AntdFormItem {...fieldProps}>
        <CustomSelect ref={ref} {...props} options={CurrencyOptions} />
      </AntdFormItem>
    );
  },
);

export const AntdFormMonth: React.FC<AntdFormPredefinedSelectProps> = forwardRef(
  ({ fieldProps, ...props }, ref) => {
    return (
      <AntdFormItem {...fieldProps}>
        <CustomSelect
          ref={ref}
          {...props}
          options={Array.from({ length: 12 }).map((_, index) => ({
            label: `${index + 1}`,
            value: index + 1,
          }))}
        />
      </AntdFormItem>
    );
  },
);
